import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { listVideos, updateProfile } from "../lib/api";
import { useAuth } from "../auth";
import { PLATFORM_LABELS } from "../lib/youtube";

const Field = ({ testid, ...props }) => (
  <input
    data-testid={testid}
    {...props}
    className="w-full bg-white/5 border border-white/10 rounded-xl px-5 py-3.5 text-white placeholder-zinc-500 focus:outline-none focus:border-white/30 focus:bg-white/10 transition-colors duration-300 font-ui text-sm"
  />
);

const Stat = ({ label, value, testid }) => (
  <div className="border border-white/10 rounded-2xl p-6 bg-white/[0.02]" data-testid={testid}>
    <p className="font-heading text-4xl text-white tracking-tight">{value}</p>
    <p className="mt-2 font-meta text-[10px] uppercase tracking-[0.25em] text-zinc-500">{label}</p>
  </div>
);

export default function Profile() {
  const { user, setUser } = useAuth();
  const [firstName, setFirstName] = useState(user?.first_name || "");
  const [lastName, setLastName] = useState(user?.last_name || "");
  const [saving, setSaving] = useState(false);
  const [videos, setVideos] = useState([]);

  useEffect(() => {
    listVideos().then(setVideos).catch(() => {});
  }, []);

  const rated = videos.filter((v) => (v.rating || 0) > 0);
  const avg = rated.length ? (rated.reduce((s, v) => s + v.rating, 0) / rated.length).toFixed(1) : "—";
  const topRated = videos.filter((v) => (v.rating || 0) >= 4).length;
  const platformCounts = videos.reduce((acc, v) => {
    acc[v.platform] = (acc[v.platform] || 0) + 1;
    return acc;
  }, {});
  const favPlatform = Object.entries(platformCounts).sort((a, b) => b[1] - a[1])[0]?.[0];

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const u = await updateProfile({ first_name: firstName, last_name: lastName });
      setUser(u);
      toast.success("Profile updated");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update your profile");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="profile-page" className="max-w-3xl mx-auto py-16 px-6 w-full">
      <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}>
        <p className="font-meta text-[10px] uppercase tracking-[0.35em] text-zinc-600 mb-3">Your Seat</p>
        <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight text-white mb-12">Profile</h1>

        <div className="flex items-center gap-6 mb-12">
          {user?.picture ? (
            <img
              src={user.picture}
              alt=""
              data-testid="profile-avatar"
              referrerPolicy="no-referrer"
              className="w-20 h-20 rounded-full border border-white/20 object-cover"
            />
          ) : (
            <span
              data-testid="profile-avatar"
              className="w-20 h-20 rounded-full bg-white/10 border border-white/15 flex items-center justify-center font-heading text-2xl text-zinc-200"
            >
              {(user?.first_name?.[0] || "?").toUpperCase()}
              {(user?.last_name?.[0] || "").toUpperCase()}
            </span>
          )}
          <div>
            <p data-testid="profile-name" className="font-heading text-2xl text-white">
              {user?.first_name} {user?.last_name}
            </p>
            <p data-testid="profile-email" className="font-meta text-xs text-zinc-500 mt-1">{user?.email}</p>
          </div>
        </div>

        <form onSubmit={save} className="border border-white/10 rounded-2xl p-6 md:p-8 bg-white/[0.02] mb-12">
          <p className="font-meta text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-5">Edit your name</p>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field testid="profile-first-name-input" placeholder="First name" value={firstName} onChange={(e) => setFirstName(e.target.value)} required />
            <Field testid="profile-last-name-input" placeholder="Last name" value={lastName} onChange={(e) => setLastName(e.target.value)} required />
          </div>
          <button
            data-testid="profile-save-button"
            type="submit"
            disabled={saving}
            className="mt-5 px-8 py-3 rounded-full bg-white text-black font-meta text-xs uppercase tracking-[0.25em] hover:scale-[1.02] active:scale-[0.98] transition-transform duration-200 disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </form>

        <p className="font-meta text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-5">Archive stats</p>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="profile-stats">
          <Stat testid="stat-films" label="Films Logged" value={videos.length} />
          <Stat testid="stat-top-rated" label="Top Rated (4★+)" value={topRated} />
          <Stat testid="stat-avg-rating" label="Avg Rating" value={avg} />
          <Stat testid="stat-platform" label="Favourite Platform" value={favPlatform ? PLATFORM_LABELS[favPlatform] : "—"} />
        </div>
      </motion.div>
    </div>
  );
}
